import React, { useEffect, useState } from 'react';
import { G, styles, api, fmtUsd } from '../theme.js';
import { Btn, Badge, Input, Select, Field, Modal, Spinner, Tog, Textarea } from '../ui.jsx';

export function Growth({ toast, user }) {
  const [tab, setTab] = useState('coach');
  return <div style={{ padding:24 }}>
    <div style={{ marginBottom:18 }}>
      <h1 style={{ margin:0, fontSize:24, fontWeight:800 }}>Growth + Revenue</h1>
      <div style={{ fontSize:13, color:G.textMuted, marginTop:4 }}>Your Coach (auto-emailed weekly), Reputation Management, Customer Financing, Predictive Maintenance, and White-label branding.</div>
    </div>
    <div style={{ display:'flex', gap:6, marginBottom:18, background:G.input, padding:4, borderRadius:10, width:'fit-content', flexWrap:'wrap' }}>
      {[['coach','📊 Coach'],['reputation','⭐ Reputation'],['financing','💳 Financing'],['predictive','🔮 Predictive'],['whitelabel','🎨 White-label']].map(([id,l]) => (
        <Btn key={id} sz="sm" v={tab===id?'primary':'ghost'} onClick={()=>setTab(id)}>{l}</Btn>
      ))}
    </div>
    {tab==='coach' && <CoachTab toast={toast}/>}
    {tab==='reputation' && <ReputationTab toast={toast}/>}
    {tab==='financing' && <FinancingTab toast={toast}/>}
    {tab==='predictive' && <PredictiveTab toast={toast}/>}
    {tab==='whitelabel' && <WhitelabelTab toast={toast} user={user}/>}
  </div>;
}

function CoachTab({ toast }) {
  const [data, setData] = useState(null);
  useEffect(() => { api('/api/growth/coach/insights').then(setData).catch(()=>{}); }, []);
  if (!data) return <Spinner sz={32}/>;

  return <div>
    <div style={{ ...styles.card, padding:18, marginBottom:14, borderColor: G.purple+'66', background:`linear-gradient(135deg,${G.purpleDim},${G.cyanLight})` }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
        <div>
          <div style={{ fontSize:11, color:G.purple, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em' }}>📊 TRADEOPS COACH — AUTO-DELIVERED EVERY MONDAY</div>
          <h3 style={{ margin:'4px 0 0 0', fontSize:18 }}>Week of {data.weekOf}</h3>
        </div>
        <Badge c={G.green} bg={G.greenLight}>Auto-emailed weekly</Badge>
      </div>
    </div>

    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(140px,1fr))', gap:10, marginBottom:14 }}>
      <CStat label="Jobs done" value={data.summary.completedJobs}/>
      <CStat label="Collected" value={fmtUsd(data.summary.collectedThisWeek)}/>
      <CStat label="Close rate" value={`${data.summary.closeRate}%`} c={data.summary.closeRate>=50?G.green:data.summary.closeRate>=30?G.yellow:G.red}/>
      <CStat label="New leads" value={data.summary.newLeads}/>
      <CStat label="AI book rate" value={`${data.summary.aiBookRate||0}%`}/>
    </div>

    {data.wins.length > 0 && <div style={{ ...styles.card, padding:14, marginBottom:10, borderColor:G.green+'66' }}>
      <div style={{ fontSize:12, fontWeight:700, color:G.green, marginBottom:6 }}>✅ WINS</div>
      {data.wins.map((w,i) => <div key={i} style={{ fontSize:13, padding:'3px 0' }}>• {w}</div>)}
    </div>}

    {data.insights.length > 0 && <div style={{ ...styles.card, padding:14, marginBottom:10, borderColor:G.yellow+'66' }}>
      <div style={{ fontSize:12, fontWeight:700, color:G.yellow, marginBottom:6 }}>⚠️ ATTENTION</div>
      {data.insights.map((i,idx) => (
        <div key={idx} style={{ padding:'8px 0', borderBottom: idx < data.insights.length-1?`1px solid ${G.border}`:'none' }}>
          <strong style={{ fontSize:13 }}>{i.title}</strong>
          <div style={{ fontSize:12, color:G.textMuted, marginTop:2 }}>{i.detail}</div>
          {i.action && <div style={{ fontSize:12, color:G.purple, marginTop:4, fontWeight:600 }}>→ {i.action}</div>}
        </div>
      ))}
    </div>}

    {data.suggestions.length > 0 && <div style={{ ...styles.card, padding:14 }}>
      <div style={{ fontSize:12, fontWeight:700, color:G.cyan, marginBottom:6 }}>💡 ONE-CLICK ACTIONS</div>
      {data.suggestions.map((s,idx) => (
        <div key={idx} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'6px 0', fontSize:13, borderBottom: idx < data.suggestions.length-1?`1px solid ${G.border}`:'none' }}>
          <span>{s.title}</span>
          <Badge c={G.cyan} bg={G.cyanLight}>{s.impact}</Badge>
        </div>
      ))}
    </div>}
  </div>;
}

function CStat({ label, value, c }) {
  return <div style={{ ...styles.card, padding:10, background:G.input }}>
    <div style={{ fontSize:10, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em' }}>{label}</div>
    <div style={{ fontSize:20, fontWeight:800, color: c||G.text, marginTop:2 }}>{value}</div>
  </div>;
}

function ReputationTab({ toast }) {
  const [cfg, setCfg] = useState(null);
  const [reviews, setReviews] = useState([]);
  useEffect(() => { Promise.all([api('/api/growth/reputation/config'), api('/api/growth/reputation/reviews')]).then(([c,r]) => { setCfg(c); setReviews(r); }); }, []);
  if (!cfg) return <Spinner sz={32}/>;
  const save = async () => { try { await api('/api/growth/reputation/config', { method:'PATCH', body: cfg }); toast('Saved'); } catch(e) { toast(e.message,'error'); }};
  return <>
    <div style={{ ...styles.card, padding:18, marginBottom:14 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
        <h3 style={{ margin:0 }}>Reputation Management</h3>
        <Tog on={cfg.enabled} onChange={()=>setCfg({...cfg, enabled:!cfg.enabled})}/>
      </div>
      <div style={{ fontSize:13, color:G.textMuted, marginBottom:14 }}>Auto-route review requests post-payment. Customers rating ≥{cfg.threshold} stars go to public Google. Below that get a private feedback form so issues get resolved before going public.</div>
      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
        <Field label="Star threshold for public Google review">
          <Select value={cfg.threshold} onChange={e=>setCfg({...cfg, threshold:Number(e.target.value)})}>
            {[3,4,5].map(n => <option key={n} value={n}>{n}+ stars</option>)}
          </Select>
        </Field>
        <Field label="Google Place ID"><Input value={cfg.gbpPlaceId||''} onChange={e=>setCfg({...cfg, gbpPlaceId:e.target.value})}/></Field>
        <Field label="Google Review URL" style={{ gridColumn:'1/-1' }}><Input value={cfg.gbpReviewUrl||''} onChange={e=>setCfg({...cfg, gbpReviewUrl:e.target.value})} placeholder="https://g.page/r/..."/></Field>
      </div>
      <Btn v="primary" onClick={save}>Save</Btn>
    </div>
    <div style={{ ...styles.card, padding:0, overflow:'hidden' }}>
      <table style={{ width:'100%', borderCollapse:'collapse' }}>
        <thead><tr>{['Date','Customer','Rating','Routed To','Comment'].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
        <tbody>{reviews.map(r => <tr key={r.id}>
          <td style={{ ...styles.td, fontSize:12 }}>{new Date(r.created_at).toLocaleDateString()}</td>
          <td style={styles.td}>{r.first_name} {r.last_name}</td>
          <td style={styles.td}><Badge c={r.rating>=4?G.green:r.rating>=3?G.yellow:G.red} bg={(r.rating>=4?G.green:r.rating>=3?G.yellow:G.red)+'22'}>{'★'.repeat(r.rating||0)}</Badge></td>
          <td style={styles.td}><Badge c={r.routed_to==='google'?G.green:G.purple} bg={(r.routed_to==='google'?G.green:G.purple)+'22'}>{r.routed_to}</Badge></td>
          <td style={{ ...styles.td, fontSize:12, color:G.textMuted }}>{r.comment||'—'}</td>
        </tr>)}</tbody>
      </table>
      {reviews.length===0 && <div style={{ padding:32, textAlign:'center', color:G.textMuted }}>No reviews yet. Reviews appear here when customers respond to the post-payment workflow.</div>}
    </div>
  </>;
}

function FinancingTab({ toast }) {
  const [partners, setPartners] = useState({});
  const [reqs, setReqs] = useState([]);
  const [config, setConfig] = useState({ markupPct: 0 });
  const [exploreAmount, setExploreAmount] = useState(9000);
  const [recommendation, setRecommendation] = useState(null);

  useEffect(() => {
    Promise.all([
      api('/api/growth/financing/partners'),
      api('/api/growth/financing/requests'),
      api('/api/growth/financing/config'),
    ]).then(([p,r,c]) => { setPartners(p); setReqs(r); setConfig(c); });
  }, []);

  const explore = async (amount) => {
    try { const r = await api(`/api/growth/financing/recommend?amount=${amount}`); setRecommendation(r); }
    catch(e) { toast(e.message,'error'); }
  };
  useEffect(() => { explore(exploreAmount); }, [config.markupPct]);

  const saveConfig = async () => {
    try { await api('/api/growth/financing/config', { method:'PATCH', body: { markupPct: config.markupPct }}); toast('Saved'); }
    catch(e) { toast(e.message,'error'); }
  };

  const approved = reqs.filter(r => r.status === 'approved').length;
  return <>
    <div style={{ ...styles.card, padding:18, marginBottom:14, borderColor:G.green+'66', background: G.greenLight }}>
      <h3 style={{ margin:'0 0 6px 0' }}>💳 Customer Financing</h3>
      <div style={{ fontSize:13, color:G.textMuted, lineHeight:1.5 }}>
        Customers click "Finance" on estimates → instant approval → close rate jumps 25-40%. All approved financing partners shown below — recommended one is highlighted.
      </div>
    </div>

    {/* Partner rate explorer */}
    <div style={{ ...styles.card, padding:18, marginBottom:14 }}>
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:14 }}>
        <h3 style={{ margin:0, fontSize:16 }}>🔍 Lender Comparison + Auto-Recommend</h3>
        <div style={{ display:'flex', alignItems:'center', gap:6 }}>
          <span style={{ fontSize:12, color:G.textMuted }}>Job amount:</span>
          <Input type="number" value={exploreAmount} onChange={e=>setExploreAmount(Number(e.target.value)||0)} style={{ width:110 }}/>
          <Btn sz="sm" onClick={()=>explore(exploreAmount)}>Recompute</Btn>
        </div>
      </div>
      {recommendation && <div style={{ fontSize:12, color:G.textMuted, marginBottom:10 }}>{recommendation.reasoning}</div>}
      {recommendation && <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(280px,1fr))', gap:10 }}>
        {(recommendation.partners||[]).map(p => (
          <div key={p.key} style={{ ...styles.card, padding:14, borderColor: p.isRecommended?G.green+'66':G.cardBorder, background: p.isRecommended?G.greenLight:G.card }}>
            <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:6 }}>
              <strong>{p.label}</strong>
              {p.isRecommended && <Badge c={G.green} bg={G.green+'33'}>⭐ Best fit</Badge>}
            </div>
            <div style={{ fontSize:11, color:G.textMuted }}>{p.bestFor}</div>
            <div style={{ marginTop:8, fontSize:12 }}>
              <div><strong>{p.typicalApr}</strong> APR · ${p.minAmount.toLocaleString()}–${p.maxAmount.toLocaleString()}</div>
              <div style={{ color:G.textMuted, marginTop:2 }}>Approval: ~{p.typicalApprovalSeconds}s · Min FICO: {p.minCreditScore}</div>
            </div>
            {p.payments?.length > 0 && <div style={{ marginTop:10, padding:8, background:G.input, borderRadius:6 }}>
              <div style={{ fontSize:10, color:G.textMuted, fontWeight:700, textTransform:'uppercase', marginBottom:4 }}>Customer's monthly payment (all-in)</div>
              {p.payments.slice(0,3).map(pay => (
                <div key={pay.termMonths} style={{ display:'flex', justifyContent:'space-between', fontSize:12, padding:'2px 0' }}>
                  <span>{pay.termMonths}mo</span>
                  <strong>{fmtUsd(pay.monthlyPaymentAllIn)}/mo</strong>
                </div>
              ))}
              {/* Transaction fee disclosure hidden from sub-accounts — TradeOps-only */}
            </div>}
          </div>
        ))}
      </div>}
    </div>

    <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:12, marginBottom:14 }}>
      <div style={{ ...styles.card, padding:14, textAlign:'center' }}>
        <div style={{ fontSize:11, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em' }}>Total Requests</div>
        <div style={{ fontSize:28, fontWeight:800, color:G.cyan, marginTop:4 }}>{reqs.length}</div>
      </div>
      <div style={{ ...styles.card, padding:14, textAlign:'center' }}>
        <div style={{ fontSize:11, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em' }}>Approved</div>
        <div style={{ fontSize:28, fontWeight:800, color:G.green, marginTop:4 }}>{approved}</div>
      </div>
      <div style={{ ...styles.card, padding:14, textAlign:'center' }}>
        <div style={{ fontSize:11, color:G.textMuted, textTransform:'uppercase', letterSpacing:'.05em' }}>Total Financed</div>
        <div style={{ fontSize:28, fontWeight:800, color:G.purple, marginTop:4 }}>{fmtUsd(reqs.reduce((s,r)=>s + (r.approved_amount||0), 0))}</div>
      </div>
    </div>

    <div style={{ ...styles.card, padding:0, overflow:'hidden' }}>
      <table style={{ width:'100%', borderCollapse:'collapse' }}>
        <thead><tr>{['Created','Customer','Amount','Status'].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
        <tbody>{reqs.map(r => <tr key={r.id}>
          <td style={{ ...styles.td, fontSize:12 }}>{new Date(r.created_at).toLocaleDateString()}</td>
          <td style={styles.td}>{r.contact_id ? '—' : 'Direct'}</td>
          <td style={{ ...styles.td, fontWeight:600 }}>{fmtUsd(r.amount)}</td>
          <td style={styles.td}><Badge c={r.status==='approved'?G.green:G.cyan} bg={(r.status==='approved'?G.green:G.cyan)+'22'}>{r.status}</Badge></td>
        </tr>)}</tbody>
      </table>
      {reqs.length===0 && <div style={{ padding:32, textAlign:'center', color:G.textMuted }}>No requests yet. The "Finance" button appears automatically on every estimate you send.</div>}
    </div>
  </>;
}

function AbTab({ toast }) {
  const [tests, setTests] = useState([]);
  useEffect(() => { api('/api/growth/ab-tests').then(setTests); }, []);
  return <>
    <div style={{ ...styles.card, padding:14, marginBottom:14, fontSize:13, color:G.textMuted, lineHeight:1.5 }}>
      Run A/B tests on AI playbook variants. Each conversation is randomly assigned to A or B. Once both have ≥50 events with an 8%+ gap, the system auto-declares a winner.
    </div>
    <div style={{ ...styles.card, padding:0, overflow:'hidden' }}>
      <table style={{ width:'100%', borderCollapse:'collapse' }}>
        <thead><tr>{['Name','A wins','B wins','Status','Winner'].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
        <tbody>{tests.map(t => <tr key={t.id}>
          <td style={styles.td}>{t.name}</td>
          <td style={styles.td}>{t.a_wins}/{t.a_count} ({t.a_count?Math.round(t.a_wins/t.a_count*100):0}%)</td>
          <td style={styles.td}>{t.b_wins}/{t.b_count} ({t.b_count?Math.round(t.b_wins/t.b_count*100):0}%)</td>
          <td style={styles.td}><Badge c={t.status==='completed'?G.green:G.cyan} bg={(t.status==='completed'?G.green:G.cyan)+'22'}>{t.status}</Badge></td>
          <td style={styles.td}>{t.winner||'—'}</td>
        </tr>)}</tbody>
      </table>
      {tests.length===0 && <div style={{ padding:32, textAlign:'center', color:G.textMuted }}>No tests yet.</div>}
    </div>
  </>;
}

function MarketplaceTab({ toast }) {
  const [leads, setLeads] = useState([]);
  const [kits, setKits] = useState([]);
  useEffect(() => { Promise.all([api('/api/growth/marketplace/leads'), api('/api/growth/marketplace/kits')]).then(([l,k]) => { setLeads(l); setKits(k); }); }, []);
  const buyLead = async id => { try { await api(`/api/growth/marketplace/leads/${id}/buy`, { method:'POST' }); toast('Lead purchased'); api('/api/growth/marketplace/leads').then(setLeads); } catch(e) { toast(e.message,'error'); }};
  const buyKit = async id => { try { await api(`/api/growth/marketplace/kits/${id}/buy`, { method:'POST' }); toast('Kit installed'); api('/api/growth/marketplace/kits').then(setKits); } catch(e) { toast(e.message,'error'); }};
  return <>
    <h3 style={{ margin:'0 0 10px 0', fontSize:16 }}>📥 Available leads from peer businesses</h3>
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:10, marginBottom:24 }}>
      {leads.map(l => (
        <div key={l.id} style={{ ...styles.card, padding:14 }}>
          <div style={{ display:'flex', justifyContent:'space-between' }}>
            <div>
              <strong>{l.service||'Service request'}</strong>
              <div style={{ fontSize:12, color:G.textMuted }}>{l.city}, {l.state}</div>
            </div>
            <Badge c={G.green} bg={G.greenLight}>{fmtUsd(l.asking_price)}</Badge>
          </div>
          {l.inServiceArea && <Badge c={G.cyan} bg={G.cyanLight} style={{ marginTop:6 }}>In your area</Badge>}
          <Btn v="primary" sz="sm" full onClick={()=>buyLead(l.id)} style={{ marginTop:10 }}>Buy this lead</Btn>
        </div>
      ))}
      {leads.length===0 && <div style={{ ...styles.card, padding:24, color:G.textMuted, textAlign:'center', gridColumn:'1/-1' }}>No leads available right now.</div>}
    </div>

    <h3 style={{ margin:'0 0 10px 0', fontSize:16 }}>🛒 Industry kits from outside experts</h3>
    <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(280px,1fr))', gap:10 }}>
      {kits.map(k => (
        <div key={k.id} style={styles.card}>
          <strong>{k.snapshot_name}</strong>
          <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>{k.description||''}</div>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginTop:10 }}>
            <Badge c={G.purple} bg={G.purpleDim}>{k.industry}</Badge>
            <strong style={{ color:G.green }}>{fmtUsd(k.price)}</strong>
          </div>
          <Btn v="primary" sz="sm" full onClick={()=>buyKit(k.id)} style={{ marginTop:8 }}>Install</Btn>
        </div>
      ))}
      {kits.length===0 && <div style={{ ...styles.card, padding:24, color:G.textMuted, textAlign:'center', gridColumn:'1/-1' }}>No kits available yet. Sellers can list their snapshots — we take 30%.</div>}
    </div>
  </>;
}

function PredictiveTab({ toast }) {
  const [alerts, setAlerts] = useState([]);
  const load = () => api('/api/growth/predictive/alerts').then(setAlerts);
  useEffect(() => { load(); }, []);
  const scan = async () => { try { const r = await api('/api/growth/predictive/scan', { method:'POST' }); toast(`${r.created.length} new alerts`); load(); } catch(e) { toast(e.message,'error'); }};
  const dismiss = async id => { try { await api(`/api/growth/predictive/alerts/${id}/dismiss`, { method:'POST' }); load(); } catch(e) { toast(e.message,'error'); }};
  return <>
    <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:14 }}>
      <h3 style={{ margin:0 }}>🔮 Predictive Maintenance Alerts</h3>
      <Btn v="primary" onClick={scan}>🔍 Run scan</Btn>
    </div>
    <div style={{ ...styles.card, padding:14, marginBottom:14, fontSize:13, color:G.textMuted, lineHeight:1.5 }}>
      Predicts equipment failures from age + condition + service history. Real version pulls live data from connected smart thermostats.
    </div>
    <div style={{ ...styles.card, padding:0, overflow:'hidden' }}>
      <table style={{ width:'100%', borderCollapse:'collapse' }}>
        <thead><tr>{['Customer','Equipment','Type','Confidence','Reasoning','Predicted by',''].map(h => <th key={h} style={styles.th}>{h}</th>)}</tr></thead>
        <tbody>{alerts.map(a => <tr key={a.id}>
          <td style={styles.td}>{a.first_name} {a.last_name}</td>
          <td style={{ ...styles.td, fontSize:12 }}>{a.manufacturer} {a.model} ({a.type})</td>
          <td style={styles.td}><Badge c={a.kind==='replacement'?G.red:a.kind==='maintenance'?G.yellow:G.cyan} bg={(a.kind==='replacement'?G.red:a.kind==='maintenance'?G.yellow:G.cyan)+'22'}>{a.kind}</Badge></td>
          <td style={styles.td}>{Math.round((a.confidence||0)*100)}%</td>
          <td style={{ ...styles.td, fontSize:12, color:G.textMuted }}>{a.reasoning}</td>
          <td style={{ ...styles.td, fontSize:12 }}>{a.predicted_failure_at ? new Date(a.predicted_failure_at).toLocaleDateString() : '—'}</td>
          <td style={styles.td}><Btn sz="xs" onClick={()=>dismiss(a.id)}>Dismiss</Btn></td>
        </tr>)}</tbody>
      </table>
      {alerts.length===0 && <div style={{ padding:32, textAlign:'center', color:G.textMuted }}>No alerts. Click "Run scan" to scan equipment.</div>}
    </div>
  </>;
}

function WhitelabelTab({ toast, user }) {
  const [cfg, setCfg] = useState(null);
  useEffect(() => { api('/api/growth/whitelabel').then(c => setCfg({
    brandName: c.brand_name||'', brandLogoUrl: c.brand_logo_url||'',
    brandPrimaryColor: c.brand_primary_color||'', brandSecondaryColor: c.brand_secondary_color||'',
    customDomain: c.custom_domain||'',
  })); }, []);
  if (!cfg) return <Spinner sz={32}/>;
  const isElite = user?.account?.plan === 'Elite' || user?.role === 'super_admin';
  const save = async () => { try { await api('/api/growth/whitelabel', { method:'PATCH', body: cfg }); toast('Brand saved'); } catch(e) { toast(e.message,'error'); }};

  if (!isElite) {
    return <div>
      <div style={{ ...styles.card, padding:24, marginBottom:14, background:`linear-gradient(135deg,${G.purpleDim},${G.cyanLight})`, borderColor:G.purple+'66' }}>
        <div style={{ fontSize:11, color:G.purple, fontWeight:700, textTransform:'uppercase', letterSpacing:'.05em' }}>🎨 ELITE TIER FEATURE</div>
        <h2 style={{ margin:'4px 0 8px 0' }}>White-label TradeOps as your own platform</h2>
        <div style={{ color:G.textMuted, fontSize:14, lineHeight:1.6 }}>
          Customer-facing portals, SMS sender names, and email "from" addresses all show <strong style={{ color:G.text }}>your brand</strong>, not TradeOps.
          Park it on <code>app.yourbusiness.com</code>. Your customers never see who powers it.
        </div>
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit,minmax(220px,1fr))', gap:12, marginBottom:14 }}>
        {[
          ['Custom domain', 'app.yourbusiness.com'],
          ['Your logo + colors', 'Customer portal, SMS, email'],
          ['SMS sender name', 'Your business, not TradeOps'],
          ['Branded customer app', 'Your name on every receipt'],
          ['Branded review pages', 'Your URL, your brand'],
        ].map(([t,d]) => (
          <div key={t} style={{ ...styles.card, padding:14 }}>
            <strong style={{ fontSize:13 }}>{t}</strong>
            <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>{d}</div>
          </div>
        ))}
      </div>

      <div style={{ ...styles.card, padding:24, textAlign:'center', borderColor:G.purple+'66' }}>
        <div style={{ fontSize:13, color:G.textMuted, marginBottom:6 }}>Pricing</div>
        <div style={{ fontSize:32, fontWeight:800, color:G.purple }}>$499<span style={{ fontSize:16, color:G.textMuted, fontWeight:500 }}>/mo</span></div>
        <div style={{ fontSize:12, color:G.textMuted, marginTop:4 }}>Elite tier · One-time $1,000 setup (custom domain SSL + brand setup)</div>
        <Btn v="primary" style={{ marginTop:14 }} onClick={()=>toast('Upgrade flow coming — contact your account manager','warn')}>Upgrade to Elite</Btn>
      </div>
    </div>;
  }

  return <div style={{ ...styles.card, padding:18 }}>
    <h3 style={{ margin:'0 0 6px 0' }}>🎨 White-Label Config</h3>
    <div style={{ fontSize:13, color:G.textMuted, marginBottom:14 }}>Customer portal pages, emails, SMS sender name all use these.</div>
    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:14 }}>
      <Field label="Brand Name (overrides 'TradeOps' on customer pages)"><Input value={cfg.brandName} onChange={e=>setCfg({...cfg, brandName:e.target.value})}/></Field>
      <Field label="Logo URL"><Input value={cfg.brandLogoUrl} onChange={e=>setCfg({...cfg, brandLogoUrl:e.target.value})}/></Field>
      <Field label="Primary Color (hex)"><Input value={cfg.brandPrimaryColor} onChange={e=>setCfg({...cfg, brandPrimaryColor:e.target.value})} placeholder="#8b5cf6"/></Field>
      <Field label="Secondary Color"><Input value={cfg.brandSecondaryColor} onChange={e=>setCfg({...cfg, brandSecondaryColor:e.target.value})} placeholder="#06b6d4"/></Field>
      <Field label="Custom Domain" style={{ gridColumn:'1/-1' }}><Input value={cfg.customDomain} onChange={e=>setCfg({...cfg, customDomain:e.target.value})} placeholder="app.yourbusiness.com"/></Field>
    </div>
    <Btn v="primary" onClick={save} style={{ marginTop:14 }}>Save Brand</Btn>
  </div>;
}
